import { useMutation, useQueryClient } from '@tanstack/react-query'

import { errorToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'

import { ProfileAttributeItemResponse, updateCustomerAttributes } from '../api'
import { keys } from '../queryKeys'

type CustomerAttributeUpdateParams = {
  attributeId: string
  catalogueAttributeId: string
  region: string
  description: string
  isUsed: boolean
}

const useUpdateCustomerAttributesMutation = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const queryClient = useQueryClient()

  return useMutation<
    ProfileAttributeItemResponse[],
    Error,
    CustomerAttributeUpdateParams,
    {
      previousData: ProfileAttributeItemResponse[] | undefined
      queryKey: unknown[]
    }
  >({
    mutationFn: ({
      attributeId,
      catalogueAttributeId,
      region,
      description,
      isUsed,
    }) =>
      updateCustomerAttributes({
        accountId,
        attributeId,
        description,
        catalogueAttributeId,
        region,
        isUsed,
      }),
    onMutate: async (variables) => {
      const queryKey = keys.customerAttributes({
        accountId,
        catalogueAttributeId: variables.catalogueAttributeId,
        region: variables.region,
      })

      await queryClient.cancelQueries({ queryKey })

      const previousData =
        queryClient.getQueryData<ProfileAttributeItemResponse[]>(queryKey)

      queryClient.setQueryData<ProfileAttributeItemResponse[]>(
        queryKey,
        (old) => {
          if (!old) {
            return old
          }
          return old.map((attribute) =>
            attribute.id === variables.attributeId
              ? { ...attribute, isUsed: variables.isUsed }
              : attribute
          )
        }
      )

      return { previousData, queryKey }
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.customerAttributes({
          accountId,
          catalogueAttributeId: variables.catalogueAttributeId,
          region: variables.region,
        }),
      })
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData)
      }
      errorToast('Failed to update the attribute')
    },
  })
}

export default useUpdateCustomerAttributesMutation
